import { tFunctionType, useI18n } from 'app/components/localization';
import AlertBox, { AlertVariant } from 'app/components/presentation/alert/AlertBox';
import { ButtonA, ColorScheme } from 'app/components/presentation/buttons/ButtonClassNameA';
import {
  DeviceType,
  useDeviceManager,
  useDeviceManagerState,
  useIsDeviceManagerBusy,
} from 'app/containers/rtc/DeviceManagerContext';
import TrackState from 'app/services/rtc/TrackState';
import React, { FunctionComponent, memo, useCallback } from 'react';

interface IProps {
  readonly type: DeviceType;
}

const DeviceWarning: FunctionComponent<IProps> = memo(({ type }) => {
  const { t } = useI18n();
  const manager = useDeviceManager();
  const devices = useDeviceManagerState();
  const busy = useIsDeviceManagerBusy();
  const { trackState } = devices[type];

  const unmute = useCallback(() => {
    return type === DeviceType.Audio ? manager.setAudioEnabled(true) : manager.setVideoEnabled(true);
  }, [type, manager]);

  if (!devices) {
    return null;
  }

  if (trackState === TrackState.Muted) {
    return <AlertBox role="alert" variant={AlertVariant.Warning}>{getDeviceMutedWarning(t, type)}</AlertBox>;
  } else if (trackState === TrackState.Disabled) {
    return (
      <ButtonA colorScheme={ColorScheme.Secondary} disabled={busy} onClick={unmute}>
        {t('rtc.playback.unmute')}
      </ButtonA>
    );
  } else {
    return null;
  }
});

export const getDeviceMutedWarning = (t: tFunctionType, type: DeviceType): string => {
  switch (type) {
    case DeviceType.Audio:
      return t('rtc.device-setup.muted-in-os.audio');
    case DeviceType.Video:
      return t('rtc.device-setup.muted-in-os.video');
  }
};

export default DeviceWarning;
